import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { portalApi } from '../../../../lib/portalApi';
import ConfirmDeleteModal from '../../shared/ConfirmDeleteModal';
import { SearchableDocumentsModal } from '../../shared/SearchableStartupDocuments';
import AdminMentorControls from '../components/AdminMentorControls';
import { Award, FolderOpen } from 'lucide-react';

export function AdminBatchesPage() {
  const [batches, setBatches] = useState([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = () => portalApi.listBatches().then(setBatches).catch(() => setBatches([]));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    try {
      await portalApi.createBatch({ name: name.trim(), year: new Date().getFullYear() });
      setName('');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteBatch(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete batch');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Batches</h1>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <form onSubmit={create} className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New batch name"
          className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2 text-white text-sm"
        />
        <button type="submit" className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm">
          Create
        </button>
      </form>
      <div className="space-y-2">
        {batches.map((b) => (
          <div
            key={b.id}
            className="flex justify-between items-center gap-3 rounded-xl border border-white/10 px-4 py-3"
          >
            <Link to={`/admin/batches/${b.id}`} className="min-w-0 flex-1 hover:opacity-90">
              <div className="text-white font-medium">{b.name}</div>
              <div className="text-xs text-white/40">
                {b.year} · {b.startupCount || 0} startups
              </div>
            </Link>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-[#ff8ec5]">{b.status}</span>
              <button
                type="button"
                onClick={() => {
                  setDeleteTarget(b);
                  setDeleteError('');
                }}
                className="text-xs text-red-400/90 hover:text-red-300 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDelete}
        title="Delete Batch"
        entityName={deleteTarget?.name}
        entityDetail={`${deleteTarget?.year || ''} · ${deleteTarget?.startupCount || 0} startups · ${deleteTarget?.status || ''}`}
        description="Are you sure you want to delete this accelerator batch? This action cannot be undone."
        warning="If this batch contains active startups, deletion will be blocked until startups are reallocated or removed. Startups and founder accounts will not be deleted."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete Batch"
      />
    </div>
  );
}

export function AdminBatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const load = () => portalApi.getBatch(id).then(setBatch);
  useEffect(() => {
    load().catch(() => setBatch(null));
  }, [id]);

  if (!batch) return <p className="text-white/40">Loading…</p>;

  const confirmDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteBatch(id);
      navigate('/admin/batches');
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete batch');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/batches" className="text-sm text-white/50 hover:text-[#ff8ec5]">
        ← Batches
      </Link>
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">{batch.name}</h1>
          <p className="text-white/50 text-sm">
            {batch.code} · {batch.year} · {batch.status}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => portalApi.activateBatch(id).then(load)}
            className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm"
          >
            Activate
          </button>
          <button
            type="button"
            onClick={() => portalApi.closeBatch(id).then(load)}
            className="px-3 py-2 rounded-xl border border-white/15 text-white text-sm"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              setDeleteOpen(true);
              setDeleteError('');
            }}
            className="px-3 py-2 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm transition-colors"
          >
            Delete Batch
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-white font-medium">Startups</h3>
        {(batch.startups || []).length === 0 && (
          <p className="text-white/40 text-sm">No startups assigned yet.</p>
        )}
        {(batch.startups || []).map((s) => (
          <div
            key={s.id}
            className="flex justify-between rounded-xl border border-white/10 px-4 py-3"
          >
            <div>
              <div className="text-white">{s.name}</div>
              <div className="text-xs text-white/40">{s.founderEmail}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Link
                to={`/admin/batches/${id}/startups/${s.id}`}
                className="text-xs text-[#ff8ec5]"
              >
                Dossier
              </Link>
              {s.stage === 'accepted' && (
                <Link to={`/admin/onboarding/${s.id}`} className="text-xs text-white/40">
                  Onboarding
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteOpen(false);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDelete}
        title="Delete Batch"
        entityName={batch.name}
        entityDetail={`${batch.code || ''} · ${batch.year || ''} · ${(batch.startups || []).length} startup(s) · ${batch.status}`}
        description="Are you sure you want to delete this accelerator batch? This action cannot be undone."
        warning="If this batch contains active startups, deletion will be blocked until startups are reallocated or removed. Startups and founder accounts will not be deleted."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete Batch"
      />
    </div>
  );
}

const STARTUP_STAGES = [
  'prospect',
  'applicant',
  'accepted',
  'active_batch',
  'demo_day',
  'graduated',
  'inactive',
  'withdrawn',
];

const emptyStartupForm = {
  name: '',
  founderEmail: '',
  founderName: '',
  stage: 'prospect',
  entryPath: 'apply',
  oneLiner: '',
  sector: '',
  website: '',
};

export function AdminStartupsPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyStartupForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [docsStartup, setDocsStartup] = useState(null);

  const load = () =>
    portalApi
      .listStartups()
      .then(setRows)
      .catch(() => setRows([]));

  useEffect(() => {
    load();
  }, []);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      founderEmail: s.founderEmail || '',
      founderName: s.founderName || '',
      stage: s.stage || 'prospect',
      entryPath: s.entryPath || 'apply',
      oneLiner: s.oneLiner || '',
      sector: s.sector || '',
      website: s.website || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyStartupForm);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.founderEmail.trim()) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        name: form.name.trim(),
        founderEmail: form.founderEmail.trim(),
        founderName: form.founderName.trim(),
        lifecycleStage: form.stage,
        entryPath: form.entryPath,
        oneLiner: form.oneLiner,
        sector: form.sector,
        website: form.website,
      };
      if (editingId) await portalApi.updateStartup(editingId, body);
      else await portalApi.createStartup(body);
      cancelEdit();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteStartup(deleteTarget.id);
      if (editingId === deleteTarget.id) cancelEdit();
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete startup');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Startups</h1>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <form onSubmit={save} className="rounded-xl border border-white/10 p-4 space-y-3">
        <div className="text-sm text-white font-medium">
          {editingId ? 'Edit startup' : 'Add startup'}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            required
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          <input
            required
            type="email"
            placeholder="Founder email"
            value={form.founderEmail}
            onChange={(e) => setForm({ ...form, founderEmail: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          <input
            placeholder="Founder name"
            value={form.founderName}
            onChange={(e) => setForm({ ...form, founderName: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          />
          <select
            value={form.stage}
            onChange={(e) => setForm({ ...form, stage: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
          >
            {STARTUP_STAGES.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
          <input
            placeholder="One-liner"
            value={form.oneLiner}
            onChange={(e) => setForm({ ...form, oneLiner: e.target.value })}
            className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm sm:col-span-2"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
          >
            {busy ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
      <div className="space-y-2">
        {rows.map((s) => (
          <div
            key={s.id}
            className="flex justify-between gap-3 rounded-xl border border-white/10 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="text-white font-medium">{s.name}</div>
              <div className="text-xs text-white/40">
                {s.founderEmail} · {s.entryPath}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-xs text-[#ff8ec5]">{s.stage}</span>
              <div className="flex flex-wrap justify-end gap-2">
                <Link
                  to={`/admin/invites?role=founder&startupId=${s.id}&email=${encodeURIComponent(s.founderEmail || '')}`}
                  className="text-xs text-[#ff8ec5] hover:underline font-medium"
                >
                  Invite Founder
                </Link>
                <button
                  type="button"
                  onClick={() => setDocsStartup(s)}
                  className="text-xs text-white/50 hover:text-[#ff8ec5] flex items-center gap-1"
                  title="Manage Startup Documents & Visibility"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  <span>Documents</span>
                </button>
                {s.batchId ? (
                  <Link
                    to={`/admin/batches/${s.batchId}/startups/${s.id}`}
                    className="text-xs text-white/50 hover:text-[#ff8ec5]"
                  >
                    Dossier
                  </Link>
                ) : s.stage === 'accepted' ? (
                  <Link
                    to={`/admin/onboarding/${s.id}`}
                    className="text-xs text-white/50 hover:text-[#ff8ec5]"
                  >
                    Onboarding
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => startEdit(s)}
                  className="text-xs text-white/50 hover:text-[#ff8ec5]"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteTarget(s);
                    setDeleteError('');
                  }}
                  className="text-xs text-red-400/90 hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDelete}
        title="Delete Startup"
        entityName={deleteTarget?.name}
        entityDetail={`${deleteTarget?.founderName ? deleteTarget.founderName + ' · ' : ''}${deleteTarget?.founderEmail || ''} · Stage: ${deleteTarget?.stage || ''}`}
        description="Are you sure you want to delete this startup? This will remove the startup profile, onboarding steps, and sprint tasks."
        warning="Founder user accounts will NOT be deleted. Only startup profile and program assignments are removed."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete Startup"
      />

      {docsStartup && (
        <SearchableDocumentsModal
          isOpen={Boolean(docsStartup)}
          onClose={() => setDocsStartup(null)}
          startupId={docsStartup.id}
          title={`${docsStartup.name} · Documents Repository`}
        />
      )}
    </div>
  );
}

export { AdminFormBuilderPage } from './AdminFormBuilderPage';

export function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ fullName: '', email: '', isActive: true });
  const [busy, setBusy] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [mentorModalOpen, setMentorModalOpen] = useState(false);

  const load = () =>
    portalApi
      .listUsers()
      .then(setUsers)
      .catch(() => setUsers([]));

  useEffect(() => {
    load();
  }, []);

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      fullName: u.fullName || '',
      email: u.email || '',
      isActive: u.isActive !== false,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setBusy(true);
    setError('');
    try {
      await portalApi.updateUser(editingId, form);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await portalApi.deleteUser(deleteTarget.id);
      if (editingId === deleteTarget.id) setEditingId(null);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete user');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-white">Users</h1>
        <button
          type="button"
          onClick={() => setMentorModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-sm"
        >
          <Award className="w-4 h-4" />
          <span>Mentor Controls (KRA/KPI & Assignments)</span>
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      {editingId && (
        <form onSubmit={save} className="rounded-xl border border-white/10 p-4 space-y-3">
          <div className="text-sm text-white font-medium">Edit user</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              required
              placeholder="Full name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 rounded-xl bg-[#d42b92] text-white text-sm disabled:opacity-40"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      <div className="space-y-2">
        {users.map((u) => (
          <div
            key={u.id}
            className="rounded-xl border border-white/10 px-4 py-3 flex flex-wrap justify-between gap-3"
          >
            <div>
              <div className="text-white">
                {u.fullName}
                {u.isActive === false && (
                  <span className="ml-2 text-xs text-white/35">(inactive)</span>
                )}
              </div>
              <div className="text-xs text-white/40">{u.email}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-xs text-[#ff8ec5]">{(u.roles || []).join(', ')}</div>
              <div className="flex items-center gap-2.5">
                {((u.roles || []).includes('mentor') || u.role === 'mentor' || u.role === 'MENTOR') && (
                  <Link
                    to={`/admin/mentors/${u.id}?tab=tasks`}
                    className="text-xs text-[#ff8ec5] hover:text-white font-medium transition-colors"
                  >
                    View Mentor (KRA/KPI)
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => startEdit(u)}
                  className="text-xs text-white/50 hover:text-[#ff8ec5]"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteTarget(u);
                    setDeleteError('');
                  }}
                  className="text-xs text-red-400/90 hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => {
          if (!deleteBusy) {
            setDeleteTarget(null);
            setDeleteError('');
          }
        }}
        onConfirm={confirmDelete}
        title="Delete User"
        entityName={deleteTarget?.fullName || deleteTarget?.email}
        entityDetail={`${deleteTarget?.email || ''} · Roles: ${(deleteTarget?.roles || []).join(', ') || 'user'}`}
        description="Are you sure you want to deactivate and remove this user? Their active sessions and refresh tokens will be immediately invalidated."
        warning="If this user is the primary founder of an active startup or is a privileged administrator, deletion will be blocked by system safety rules."
        busy={deleteBusy}
        error={deleteError}
        confirmLabel="Delete User"
      />

      {mentorModalOpen && <AdminMentorControls onClose={() => setMentorModalOpen(false)} />}
    </div>
  );
}

export function AdminInvitesPage() {
  const [searchParams] = useSearchParams();
  const [invites, setInvites] = useState([]);
  const [startups, setStartups] = useState([]);
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState('founder');
  const [selectedStartupId, setSelectedStartupId] = useState('');
  const [customStartupName, setCustomStartupName] = useState('');
  const [error, setError] = useState('');
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Initialize from searchParams if provided (e.g., from Dossier or Startups list)
  useEffect(() => {
    const roleParam = searchParams.get('role');
    const startupIdParam = searchParams.get('startupId');
    const emailParam = searchParams.get('email');
    if (roleParam) {
      if (['founder', 'co_founder', 'startup'].includes(roleParam)) {
        setKind(roleParam === 'co_founder' ? 'co_founder' : 'founder');
      } else if (['mentor', 'admin', 'cxo'].includes(roleParam)) {
        setKind(roleParam);
      }
    }
    if (startupIdParam) {
      setSelectedStartupId(startupIdParam);
    }
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const load = () => {
    portalApi.listInvites().then(setInvites).catch(() => setInvites([]));
    portalApi.listStartups().then(setStartups).catch(() => setStartups([]));
  };

  useEffect(() => {
    load();
  }, []);

  // When a startup is selected and email is empty, auto-populate email from startup record
  const handleSelectStartup = (sid) => {
    setSelectedStartupId(sid);
    if (sid) {
      const match = startups.find((s) => (s.id || s._id) === sid);
      if (match && match.founderEmail && (!email || email === '')) {
        setEmail(match.founderEmail);
      }
    }
  };

  const isStartupRole = kind === 'founder' || kind === 'co_founder';

  const create = async (e) => {
    e.preventDefault();
    setError('');
    setCreatedInvite(null);
    setCopied(false);
    setSubmitting(true);
    try {
      const matchedStartup = selectedStartupId
        ? startups.find((s) => (s.id || s._id) === selectedStartupId)
        : null;
      const targetStartupName = matchedStartup ? matchedStartup.name : (customStartupName.trim() || undefined);

      const payload = {
        email: email.trim(),
        kind,
        startupId: selectedStartupId || undefined,
        startupName: targetStartupName,
      };

      const res = await portalApi.createInvite(payload);
      setCreatedInvite({
        email: email.trim(),
        kind,
        startupName: targetStartupName,
        url: res?.inviteUrl || `${window.location.origin}/invite/${res?.id}`,
      });
      setEmail('');
      load();
    } catch (err) {
      setError(err.message || 'Failed to create invite');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (createdInvite?.url) {
      navigator.clipboard.writeText(createdInvite.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const revoke = async (i) => {
    const id = i._id || i.id;
    if (!id || !window.confirm(`Revoke invite for ${i.email}?`)) return;
    setError('');
    try {
      await portalApi.revokeInvite(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-white">Invitations & Credentials Generator</h1>
        <p className="text-xs text-white/50 mt-1">
          Generate secure 24-byte cryptographic invitation links for <strong className="text-white">Startup Founders</strong>, <strong className="text-white">Mentors</strong>, and <strong className="text-white">Staff</strong>. Invitees set their own passwords and gain instant dashboard access.
        </p>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
          {error}
        </div>
      )}

      {createdInvite && (
        <div className="p-4 rounded-2xl bg-[#d42b92]/10 border border-[#d42b92]/30 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">
              Invitation Generated for <span className="text-[#ff8ec5]">{createdInvite.email}</span> (
              {createdInvite.kind === 'founder'
                ? 'Startup Founder'
                : createdInvite.kind === 'co_founder'
                ? 'Startup Co-Founder'
                : createdInvite.kind}
              {createdInvite.startupName ? ` · ${createdInvite.startupName}` : ''})
            </span>
            <span className="text-[11px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full font-medium">
              Ready to Accept
            </span>
          </div>

          <div className="flex gap-2 items-center">
            <input
              type="text"
              readOnly
              value={createdInvite.url}
              className="flex-1 bg-black/60 border border-white/15 rounded-xl px-3.5 py-2 text-xs text-white select-all font-mono"
            />
            <button
              type="button"
              onClick={copyLink}
              className="px-4 py-2 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-xs font-medium transition-colors"
            >
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <a
              href={createdInvite.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-xl border border-white/15 hover:border-white/30 text-white text-xs font-medium transition-colors inline-block"
            >
              Open Link &rarr;
            </a>
          </div>

          <p className="text-[11px] text-white/60">
            Tip: Open this link in an <strong className="text-white">Incognito / Private tab</strong> so the current administrator session is not overwritten while testing or setting credentials.
          </p>
        </div>
      )}

      <form onSubmit={create} className="space-y-3 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
        <div className="text-xs uppercase tracking-wider text-white/40 font-semibold">
          Create New Invitation
        </div>
        <div className="flex flex-wrap gap-2.5 items-center">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]"
          >
            <option value="founder">Startup Founder (Lead)</option>
            <option value="co_founder">Startup Co-Founder</option>
            <option value="mentor">Mentor</option>
            <option value="admin">Staff / Sub-Admin</option>
            <option value="cxo">CXO Advisor</option>
          </select>

          {isStartupRole && (
            <>
              <select
                value={selectedStartupId}
                onChange={(e) => handleSelectStartup(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92] max-w-xs"
              >
                <option value="">-- Select Target Startup --</option>
                {startups.map((s) => (
                  <option key={s.id || s._id} value={s.id || s._id}>
                    {s.name} {s.founderEmail ? `(${s.founderEmail})` : ''}
                  </option>
                ))}
              </select>

              {!selectedStartupId && (
                <input
                  value={customStartupName}
                  onChange={(e) => setCustomStartupName(e.target.value)}
                  type="text"
                  placeholder="Or enter new startup name"
                  className="bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92] w-60"
                />
              )}
            </>
          )}

          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            placeholder="invitee@domain.com"
            className="bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm w-72 focus:outline-none focus:border-[#d42b92]"
          />

          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl bg-[#d42b92] hover:bg-[#b02279] text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {submitting ? 'Generating…' : 'Generate Invite Link'}
          </button>
        </div>
      </form>

      <div className="space-y-2 pt-2">
        <div className="text-xs uppercase tracking-wider text-white/40 font-semibold px-1">
          Recent Invitations ({invites.length})
        </div>
        {invites.length === 0 ? (
          <div className="p-6 text-center text-xs text-white/40 border border-white/5 rounded-2xl bg-white/[0.01]">
            No invites sent yet.
          </div>
        ) : (
          invites.map((i) => (
            <div
              key={i._id || i.id}
              className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/80 flex items-center justify-between gap-3 flex-wrap"
            >
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-medium text-white">{i.email}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/70">
                  {i.kind === 'founder'
                    ? 'Startup Founder'
                    : i.kind === 'co_founder'
                    ? 'Co-Founder'
                    : i.kind}
                </span>
                {(i.startupName || i.startupId?.name) && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#d42b92]/15 text-[#ff8ec5] border border-[#d42b92]/30">
                    {i.startupName || i.startupId?.name}
                  </span>
                )}
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                    i.status === 'accepted'
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : i.status === 'pending'
                      ? 'bg-amber-500/10 text-amber-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}
                >
                  {i.status}
                </span>
              </div>
              {i.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => revoke(i)}
                  className="text-xs text-red-400 hover:text-red-300 font-medium px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors"
                >
                  Revoke
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function AdminConfigPage() {
  const links = [
    { to: '/admin/questionnaire', label: 'InnovAce apply form' },
    { to: '/admin/company-profile', label: 'Company profile' },
    { to: '/admin/due-diligence', label: 'Due Diligence' },
  ];
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Config</h1>
      <div className="flex flex-wrap gap-3">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="px-4 py-2 rounded-xl border border-white/15 text-white text-sm hover:border-[#d42b92]/40"
          >
            {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
